import React from 'react';

export type MetricIconKind = 'units' | 'orders' | 'currency' | 'rate' | 'time';

const METRIC_ICON: Record<string, MetricIconKind> = {
  'products-sold': 'units',
  'total-cyd-units': 'units',
  'total-customers': 'units',
  'new-customers': 'units',
  'recurring-customers': 'units',

  'total-orders': 'orders',
  'orders-with-cyd': 'orders',
  'open-orders': 'orders',

  'total-sales': 'currency',
  'total-cyd-amount': 'currency',
  'total-shipping-cost': 'currency',
  'average-shipping-cost': 'currency',
  'average-shipping-cost-sucursal': 'currency',
  'average-shipping-cost-domicilio': 'currency',
  'total-shipping-quoted': 'currency',
  'total-shipping-collected': 'currency',
  'shipping-collection-gap': 'currency',
  'shipping-quoted-collection-gap': 'currency',

  'cyd-rate': 'rate',
  'cyd-rate-amount': 'rate',
  'retention-rate': 'rate',

  'avg-days-sale-to-cyd': 'time',
  'avg-resolution-time': 'time',
};

export function getMetricIconKind(metricId: string): MetricIconKind {
  if (metricId in METRIC_ICON) {
    return METRIC_ICON[metricId];
  }
  if (metricId.includes('rate')) {
    return 'rate';
  }
  if (metricId.includes('time') || metricId.includes('days')) {
    return 'time';
  }
  if (metricId.includes('cost') || metricId.includes('sales') || metricId.includes('amount')) {
    return 'currency';
  }
  if (metricId.includes('order')) {
    return 'orders';
  }
  return 'units';
}

const ICON_SIZE = 20;

function IconSvg({ children }: { children: React.ReactNode }) {
  return (
    <svg
      width={ICON_SIZE}
      height={ICON_SIZE}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}

const ICONS: Record<MetricIconKind, React.ReactNode> = {
  units: (
    <IconSvg>
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </IconSvg>
  ),
  orders: (
    <IconSvg>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8" />
      <path d="M8 17h8" />
      <path d="M8 9h2" />
    </IconSvg>
  ),
  currency: (
    <svg
      width={ICON_SIZE}
      height={ICON_SIZE}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <text
        x="12"
        y="16.5"
        textAnchor="middle"
        fontSize="13"
        fontWeight="700"
        fill="currentColor"
        stroke="none"
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        $
      </text>
    </svg>
  ),
  rate: (
    <svg
      width={ICON_SIZE}
      height={ICON_SIZE}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <text
        x="12"
        y="17"
        textAnchor="middle"
        fontSize="15"
        fontWeight="700"
        fill="currentColor"
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        %
      </text>
    </svg>
  ),
  time: (
    <IconSvg>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </IconSvg>
  ),
};

export function StatCardMetricIcon({ metricId }: { metricId: string }) {
  const kind = getMetricIconKind(metricId);
  return <>{ICONS[kind]}</>;
}
